// Écran de lettrage des comptes tiers (rapprochement facture ↔ règlement).
// Sélection d'un tiers → écritures non lettrées → cocher facture(s)+règlement(s)
// → validation (complet si solde 0, partiel sinon). Délettrage réservé
// au Gérant / Administrateur.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api, { formatErreur } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { roleEffectif } from "@/lib/roles";
import { SelecteurTiers } from "@/components/SelecteurTiers";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Link2, Loader2, CheckCircle2, AlertTriangle, Unlink } from "lucide-react";

const fmt = (n) => (n ? Math.round(n).toLocaleString("fr-FR") : "0");
const anneeCourante = new Date().getFullYear();

export default function Lettrage() {
  const { user } = useAuth();
  const role = roleEffectif(user);
  const peutLettrer = ["gerant", "administrateur", "comptable"].includes(role);
  const peutDelettrer = ["gerant", "administrateur"].includes(role);

  const [exercices, setExercices] = useState([]);
  const [exercice, setExercice] = useState(String(anneeCourante));
  const [tiers, setTiers] = useState(null);
  const [data, setData] = useState(null);
  const [chargement, setChargement] = useState(false);
  const [selection, setSelection] = useState({}); // {ecritureId: true}
  const [confirmPartiel, setConfirmPartiel] = useState(false);
  const [envoi, setEnvoi] = useState(false);

  useEffect(() => {
    api.get("/exercices").then(({ data }) => {
      setExercices(data || []);
      if (data && data.length) setExercice(String(data[0].annee));
    }).catch(() => {});
  }, []);

  const charger = useCallback(async () => {
    if (!tiers?.compte) return;
    setChargement(true);
    setSelection({});
    try {
      const { data } = await api.get("/lettrage/ecritures", {
        params: { compte: tiers.compte, exercice: parseInt(exercice, 10) },
      });
      setData(data);
    } catch (e) {
      setData(null);
      toast.error(formatErreur(e.response?.data?.detail) || "Chargement impossible.");
    } finally {
      setChargement(false);
    }
  }, [tiers, exercice]);

  useEffect(() => { charger(); }, [charger]);

  const nonLettrees = useMemo(
    () => (data?.ecritures || []).filter((e) => !e.lettrage_code),
    [data]
  );
  const lettrees = useMemo(
    () => (data?.ecritures || []).filter((e) => e.lettrage_code),
    [data]
  );

  const totaux = useMemo(() => {
    let d = 0, c = 0;
    nonLettrees.forEach((e) => {
      if (selection[e.id]) { d += e.debit; c += e.credit; }
    });
    return { debit: d, credit: c, solde: d - c };
  }, [selection, nonLettrees]);

  const nbSelection = Object.values(selection).filter(Boolean).length;
  const equilibre = totaux.solde === 0;

  const toggle = (id) => setSelection((s) => ({ ...s, [id]: !s[id] }));

  const valider = async (force) => {
    setEnvoi(true);
    try {
      const ids = nonLettrees.filter((e) => selection[e.id]).map((e) => e.id);
      const { data: r } = await api.post("/lettrage", {
        compte: tiers.compte, exercice: parseInt(exercice, 10),
        ecriture_ids: ids, force_partiel: !!force,
      });
      toast.success(`Lettrage ${r.code} validé (${r.statut}).`);
      setConfirmPartiel(false);
      await charger();
    } catch (e) {
      toast.error(formatErreur(e.response?.data?.detail) || "Validation impossible.");
    } finally {
      setEnvoi(false);
    }
  };

  const delettrer = async (lettrageId, code) => {
    try {
      await api.delete(`/lettrage/${lettrageId}`);
      toast.success(`Lettrage ${code} annulé.`);
      await charger();
    } catch (e) {
      toast.error(formatErreur(e.response?.data?.detail) || "Délettrage impossible.");
    }
  };

  const Ligne = ({ e }) => (
    <tr data-testid={`ligne-ecriture-${e.id}`} className="hover:bg-[#E8F4F4]/40 border-b border-[#4A8A8A]/10">
      <td className="px-3 py-2">
        {!e.lettrage_code && peutLettrer && (
          <input type="checkbox" data-testid={`case-${e.id}`} checked={!!selection[e.id]}
            onChange={() => toggle(e.id)} className="h-4 w-4 accent-[#1D3D3D]" />
        )}
      </td>
      <td className="px-3 py-2 whitespace-nowrap text-[#1D3D3D]/80">{e.date}</td>
      <td className="px-3 py-2 font-mono text-[#4A8A8A]">{e.reference || "—"}</td>
      <td className="px-3 py-2 text-[#1D3D3D]">{e.libelle}</td>
      <td className="px-3 py-2 text-right">{e.debit ? fmt(e.debit) : "—"}</td>
      <td className="px-3 py-2 text-right">{e.credit ? fmt(e.credit) : "—"}</td>
      <td className="px-3 py-2 text-right text-[#1D3D3D]/70">{fmt(e.solde_progressif)}</td>
      <td className="px-3 py-2 text-center">
        {e.lettrage_code
          ? <span className="inline-block px-1.5 py-0.5 rounded bg-[#4A8A8A]/15 font-mono text-xs font-semibold">{e.lettrage_code}</span>
          : <span className="text-[#1D3D3D]/30">—</span>}
      </td>
    </tr>
  );

  const Bloc = ({ titre, lignes }) => (
    lignes.length > 0 && (
      <>
        <tr className="bg-[#E8F4F4]/60"><td colSpan={8} className="px-3 py-1.5 text-xs font-bold uppercase tracking-wide text-[#1D3D3D]/70">{titre}</td></tr>
        {lignes.map((e) => <Ligne key={e.id} e={e} />)}
      </>
    )
  );

  return (
    <div className="fenua-fade-up" data-testid="page-lettrage">
      <div className="flex items-center gap-3 mb-6">
        <div className="h-11 w-11 rounded-lg bg-[#E8F4F4] flex items-center justify-center">
          <Link2 className="h-6 w-6 text-[#1D3D3D]" />
        </div>
        <div>
          <h1 data-testid="titre-section" className="text-3xl font-black tracking-tight text-[#1D3D3D]">Lettrage</h1>
          <p className="text-sm text-[#1D3D3D]/70">Associez vos factures à leurs règlements pour solder les comptes tiers.</p>
        </div>
      </div>

      {/* Étape 1 — sélection du tiers + exercice */}
      <div className="bg-white border border-[#4A8A8A]/20 rounded-lg p-4 space-y-4">
        <div className="flex items-center gap-3">
          <Label className="text-xs font-semibold text-[#1D3D3D]">Exercice</Label>
          <Select value={exercice} onValueChange={setExercice}>
            <SelectTrigger data-testid="lettrage-exercice" className="w-[160px] h-9 bg-white border-[#4A8A8A]/30"><SelectValue /></SelectTrigger>
            <SelectContent>
              {(exercices.length ? exercices : [{ id: "x", annee: anneeCourante }]).map((e) => (
                <SelectItem key={e.id} value={String(e.annee)}>Exercice {e.annee}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <SelecteurTiers testId="lettrage-selecteur-tiers" onSelect={setTiers} />
      </div>

      {/* Étape 2/3 — écritures + sélection */}
      {tiers && (
        <div className="mt-6">
          {chargement ? (
            <div className="bg-white border border-[#4A8A8A]/20 rounded-lg p-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#4A8A8A]" /></div>
          ) : (
            <div className="bg-white border border-[#4A8A8A]/20 rounded-lg overflow-hidden">
              <div className="bg-[#2D5A5A] text-white px-4 py-2 font-semibold text-sm">
                Compte <span className="font-mono">{tiers.compte}</span> — {tiers.nom}
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[#E8F4F4] text-[#1D3D3D] text-left">
                    <th className="px-3 py-2 w-10"></th>
                    <th className="px-3 py-2 font-semibold">Date</th>
                    <th className="px-3 py-2 font-semibold">Pièce</th>
                    <th className="px-3 py-2 font-semibold">Libellé</th>
                    <th className="px-3 py-2 font-semibold text-right">Débit</th>
                    <th className="px-3 py-2 font-semibold text-right">Crédit</th>
                    <th className="px-3 py-2 font-semibold text-right">Solde</th>
                    <th className="px-3 py-2 font-semibold text-center">Lettrage</th>
                  </tr>
                </thead>
                <tbody data-testid="lettrage-tableau">
                  {(data?.ecritures || []).length === 0 && (
                    <tr><td colSpan={8} className="px-3 py-8 text-center text-sm text-[#1D3D3D]/60">Aucune écriture sur ce compte pour cet exercice.</td></tr>
                  )}
                  <Bloc titre="Factures" lignes={nonLettrees.filter((e) => e.groupe === "facture")} />
                  <Bloc titre="Règlements" lignes={nonLettrees.filter((e) => e.groupe === "reglement")} />
                  <Bloc titre="Autres" lignes={nonLettrees.filter((e) => e.groupe === "autre")} />
                  <Bloc titre="Écritures lettrées" lignes={lettrees} />
                </tbody>
              </table>
            </div>
          )}

          {/* Étape 3 — bandeau de sélection + validation */}
          {peutLettrer && nbSelection > 0 && (
            <div data-testid="lettrage-recap" className={`mt-4 rounded-lg p-4 border ${equilibre ? "bg-green-50 border-green-300" : "bg-amber-50 border-amber-300"}`}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="text-sm text-[#1D3D3D] flex items-center gap-2">
                  {equilibre
                    ? <CheckCircle2 className="h-5 w-5 text-green-600" />
                    : <AlertTriangle className="h-5 w-5 text-amber-600" />}
                  <span>
                    Débit <strong>{fmt(totaux.debit)}</strong> · Crédit <strong>{fmt(totaux.credit)}</strong> ·{" "}
                    {equilibre
                      ? <span className="text-green-700 font-semibold">Lettrage équilibré — vous pouvez valider</span>
                      : <span className="text-amber-700 font-semibold">Solde de {fmt(Math.abs(totaux.solde))} XPF — lettrage partiel</span>}
                  </span>
                </div>
                {equilibre ? (
                  <Button data-testid="bouton-valider-lettrage" disabled={envoi || nbSelection < 2}
                    onClick={() => valider(false)} className="bg-[#1D3D3D] hover:bg-[#2D5A5A] text-white">
                    {envoi ? <Loader2 className="h-4 w-4 animate-spin" /> : "Valider le lettrage"}
                  </Button>
                ) : (
                  <Button data-testid="bouton-valider-partiel" disabled={envoi || nbSelection < 2}
                    onClick={() => setConfirmPartiel(true)} variant="outline" className="border-amber-500 text-amber-700">
                    Valider quand même
                  </Button>
                )}
              </div>
              {nbSelection < 2 && <p className="text-xs text-[#1D3D3D]/60 mt-2">Sélectionnez au moins deux écritures.</p>}
            </div>
          )}

          {/* Lettrages existants — délettrage */}
          {data?.lettrages?.length > 0 && (
            <div className="mt-6 bg-white border border-[#4A8A8A]/20 rounded-lg overflow-hidden" data-testid="liste-lettrages">
              <div className="px-4 py-2 bg-[#E8F4F4] font-semibold text-sm text-[#1D3D3D]">Lettrages de ce compte</div>
              <table className="w-full text-sm">
                <tbody>
                  {data.lettrages.map((l) => (
                    <tr key={l.id} data-testid={`lettrage-${l.code}`} className="border-b border-[#4A8A8A]/10">
                      <td className="px-4 py-2 font-mono font-semibold">{l.code}</td>
                      <td className="px-4 py-2">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${l.statut === "complet" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>
                          {l.statut === "complet" ? "Complet" : "Partiel"}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-[#1D3D3D]/70">{l.nb_ecritures} écritures · solde {fmt(l.solde)}</td>
                      <td className="px-4 py-2 text-right">
                        {peutDelettrer && (
                          <Button data-testid={`bouton-delettrer-${l.code}`} variant="outline" size="sm"
                            onClick={() => delettrer(l.id, l.code)} className="text-red-600 border-red-200">
                            <Unlink className="h-3.5 w-3.5 mr-1" /> Délettrer
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Confirmation lettrage partiel */}
      <AlertDialog open={confirmPartiel} onOpenChange={setConfirmPartiel}>
        <AlertDialogContent data-testid="dialog-confirm-partiel">
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmer un lettrage partiel</AlertDialogTitle>
            <AlertDialogDescription>
              Le solde sélectionné n'est pas nul ({fmt(Math.abs(totaux.solde))} XPF). Ce lettrage sera
              marqué « partiel ». Voulez-vous continuer ?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction data-testid="confirmer-partiel" onClick={() => valider(true)}
              className="bg-amber-600 hover:bg-amber-700">
              Valider le lettrage partiel
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
